// Fetches one Oslo day of hourly realtime data for the report, without silently
// losing rows: the API caps a response at 10,000 rows and returns the newest
// first, so a capped slice is split in half and re-fetched until every slice is
// under the cap. The country endpoint fans out per country (~350 rows per zone
// per day), so a 50-zone project needs several slices; presence fits in one.

import { realtimeApi, RealtimeRow, RealtimeCountryRow } from "./realtimeApi";
import { osloDayRange } from "../utils/realtimeReport";

const LIMIT = 10000;
const HOUR = 3600000;

async function sliced<T>(
  fetchSlice: (fromIso: string, toIso: string) => Promise<T[]>,
  from: number,
  to: number,
  sliceMs: number
): Promise<T[]> {
  const out: T[] = [];
  const run = async (a: number, b: number): Promise<void> => {
    // datetime_to is inclusive server-side, so end each slice just before the next.
    const rows = await fetchSlice(new Date(a).toISOString(), new Date(b - 1000).toISOString());
    if (rows.length >= LIMIT && b - a > HOUR) {
      const mid = a + Math.ceil((b - a) / 2 / HOUR) * HOUR;
      await run(a, mid);
      await run(mid, b);
      return;
    }
    if (rows.length >= LIMIT) throw new Error("Too much data in a single hour to build a complete report.");
    out.push(...rows);
  };
  const starts: number[] = [];
  for (let t = from; t < to; t += sliceMs) starts.push(t);
  for (const t of starts) await run(t, Math.min(t + sliceMs, to));
  return out;
}

export interface ReportDayData {
  presence: RealtimeRow[];
  country: RealtimeCountryRow[];
  countryAvailable: boolean; // false when the account has no country breakdown (403)
}

export async function fetchReportDay(date: string): Promise<ReportDayData> {
  const { from, to } = osloDayRange(date);
  const presence = await sliced<RealtimeRow>(
    async (a, b) => (await realtimeApi.getRealtimeData({ datetimeFrom: a, datetimeTo: b, bucket: "hour", limit: LIMIT })).rows,
    from,
    to,
    to - from
  );
  try {
    const country = await sliced<RealtimeCountryRow>(
      async (a, b) => (await realtimeApi.getRealtimeDataCountry(LIMIT, undefined, a, b, "hour")).rows,
      from,
      to,
      6 * HOUR
    );
    return { presence, country, countryAvailable: true };
  } catch (err) {
    if ((err as { status?: number }).status === 403) return { presence, country: [], countryAvailable: false };
    throw err;
  }
}

/**
 * Daily country rows for every day from `fromDate` to `toDate` (inclusive), one
 * request per day so a large project stays under the row cap. Days follow the
 * API's daily buckets (UTC), so each slice is one UTC day and lands in one bucket.
 */
export async function fetchCountryDaily(fromDate: string, toDate: string): Promise<RealtimeCountryRow[]> {
  const from = Date.parse(`${fromDate}T00:00:00Z`);
  const to = Date.parse(`${toDate}T00:00:00Z`) + 24 * HOUR;
  return sliced<RealtimeCountryRow>(
    async (a, b) => (await realtimeApi.getRealtimeDataCountry(LIMIT, undefined, a, b, "day")).rows,
    from,
    to,
    24 * HOUR
  );
}
